/**
 * squads credentials — manage per-squad GCP service accounts and credentials.
 *
 * Creates, rotates, lists, and revokes service accounts so agents
 * can access the APIs they need without manual setup.
 *
 * Permissions are defined per squad in SQUAD.md:
 *   credentials:
 *     gcp:
 *       roles: [roles/bigquery.dataViewer, roles/bigquery.jobUser]
 *       apis: [bigquery.googleapis.com]
 */

import { Command } from 'commander';
import { execSync } from 'child_process';
import { existsSync, readFileSync, mkdirSync, readdirSync, unlinkSync, renameSync } from 'fs';
import { join, basename } from 'path';
import { findSquadsDir, loadSquad } from '../lib/squad-parser.js';
import { colors, bold, RESET, writeLine, icons } from '../lib/terminal.js';
import { homedir } from 'os';

// ── Permission resolution ──────────────────────────────────────────────

interface SquadPermissions {
  roles: string[];
  apis: string[];
  description: string;
}

/**
 * Parse GCP credentials config from SQUAD.md content.
 * Exported for testing. Looks for a credentials.gcp block with roles and apis.
 */
export function parseGcpCredentials(content: string): SquadPermissions | null {
  const roles: string[] = [];
  const apis: string[] = [];

  // Match: gcp:\n  roles: [role1, role2]
  const rolesMatch = content.match(/gcp:\s*\n\s*roles:\s*\[([^\]]+)\]/);
  if (rolesMatch) {
    roles.push(...rolesMatch[1].split(',').map(r => r.trim().replace(/['"]/g, '')));
  }

  // Match: gcp:\n  ...\n  apis: [api1, api2]
  const apisMatch = content.match(/gcp:\s*\n(?:.*\n)*?\s*apis:\s*\[([^\]]+)\]/);
  if (apisMatch) {
    apis.push(...apisMatch[1].split(',').map(a => a.trim().replace(/['"]/g, '')));
  }

  if (roles.length === 0 && apis.length === 0) return null;

  return {
    roles,
    apis,
    description: `${roles.length} roles, ${apis.length} APIs`,
  };
}

/**
 * Read credentials config from SQUAD.md `credentials.gcp` field.
 * Falls back to empty permissions if not defined.
 */
function getSquadPermissions(squadName: string): SquadPermissions | null {
  const squad = loadSquad(squadName);
  if (!squad) return null;

  const squadsDir = findSquadsDir();
  if (!squadsDir) return null;

  const squadMd = join(squadsDir, squadName, 'SQUAD.md');
  if (!existsSync(squadMd)) return null;

  return parseGcpCredentials(readFileSync(squadMd, 'utf-8'));
}

const SECRETS_DIR = join(homedir(), '.squads', 'secrets');
const SA_SUFFIX = '-agent';

function getProject(): string {
  try {
    return execSync('gcloud config get-value project 2>/dev/null', { encoding: 'utf-8' }).trim();
  } catch {
    throw new Error('No GCP project configured. Run: gcloud config set project <project-id>');
  }
}

function saEmail(squad: string, project: string): string {
  return `${squad}${SA_SUFFIX}@${project}.iam.gserviceaccount.com`;
}

function keyPath(squad: string): string {
  return join(SECRETS_DIR, `${squad}-sa-key.json`);
}

function ensureSecretsDir(): void {
  if (!existsSync(SECRETS_DIR)) {
    mkdirSync(SECRETS_DIR, { recursive: true });
  }
}

function gcloudExec(cmd: string, silent = false): string {
  try {
    const result = execSync(cmd, { encoding: 'utf-8', stdio: silent ? 'pipe' : ['pipe', 'inherit', 'inherit'] });
    return (result || '').trim();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('Reauthentication')) {
      throw new Error('gcloud auth expired. Run: gcloud auth login');
    }
    throw e;
  }
}

// ── Commands ────────────────────────────────────────────────────────────

async function createCredential(squad: string, opts: { force?: boolean }): Promise<void> {
  const project = getProject();
  const email = saEmail(squad, project);
  const key = keyPath(squad);
  const perms = getSquadPermissions(squad);

  if (!perms) {
    writeLine(`  ${icons.error} ${colors.red}No credentials config for squad "${squad}"${RESET}`);
    writeLine(`  ${colors.dim}Add to SQUAD.md:${RESET}`);
    writeLine(`  ${colors.dim}  credentials:${RESET}`);
    writeLine(`  ${colors.dim}    gcp:${RESET}`);
    writeLine(`  ${colors.dim}      roles: [roles/bigquery.dataViewer]${RESET}`);
    writeLine(`  ${colors.dim}      apis: [bigquery.googleapis.com]${RESET}`);
    return;
  }

  if (existsSync(key) && !opts.force) {
    writeLine(`  ${icons.warning} ${colors.yellow}Credential already exists: ${key}${RESET}`);
    writeLine(`  ${colors.dim}Use --force to recreate${RESET}`);
    return;
  }

  ensureSecretsDir();

  writeLine(`  ${bold}Creating service account for ${squad}${RESET}`);
  writeLine(`  ${colors.dim}${perms.description}${RESET}`);
  writeLine();

  // 1. Enable required APIs
  for (const api of perms.apis) {
    writeLine(`  ${colors.dim}Enabling ${api}...${RESET}`);
    try {
      gcloudExec(`gcloud services enable ${api} --project ${project} 2>/dev/null`, true);
    } catch { /* already enabled or no permission — continue */ }
  }

  // 2. Create service account (or skip if exists)
  try {
    gcloudExec(`gcloud iam service-accounts describe ${email} --project ${project} 2>/dev/null`, true);
    writeLine(`  ${colors.dim}Service account exists: ${email}${RESET}`);
  } catch {
    writeLine(`  Creating ${email}...`);
    gcloudExec(`gcloud iam service-accounts create ${squad}${SA_SUFFIX} --display-name "Squads ${squad} agent" --project ${project}`);
  }

  // 3. Grant IAM roles
  for (const role of perms.roles) {
    writeLine(`  ${colors.dim}Granting ${role}...${RESET}`);
    try {
      gcloudExec(
        `gcloud projects add-iam-policy-binding ${project} --member="serviceAccount:${email}" --role="${role}" --condition=None --quiet 2>/dev/null`,
        true,
      );
    } catch { /* role may already be bound */ }
  }

  // 4. Create and download key
  if (existsSync(key) && opts.force) {
    unlinkSync(key);
  }
  writeLine(`  ${colors.dim}Creating key...${RESET}`);
  gcloudExec(`gcloud iam service-accounts keys create ${key} --iam-account=${email} --project ${project}`);

  writeLine();
  writeLine(`  ${icons.success} ${colors.green}${squad}${RESET} credential ready`);
  writeLine(`  ${colors.dim}Key: ${key}${RESET}`);
  writeLine(`  ${colors.dim}Roles: ${perms.roles.join(', ')}${RESET}`);
  writeLine();
}

async function rotateCredential(squad: string): Promise<void> {
  const project = getProject();
  const email = saEmail(squad, project);
  const key = keyPath(squad);

  if (!existsSync(key)) {
    writeLine(`  ${icons.error} ${colors.red}No credential found for ${squad}. Run: squads credentials create ${squad}${RESET}`);
    return;
  }

  // Read old key to get key ID for deletion
  const oldKeyData = JSON.parse(readFileSync(key, 'utf-8'));
  const oldKeyId = oldKeyData.private_key_id;

  writeLine(`  ${bold}Rotating ${squad} credential${RESET}`);

  // Create new key first
  const tmpKey = key + '.new';
  gcloudExec(`gcloud iam service-accounts keys create ${tmpKey} --iam-account=${email} --project ${project}`);

  // Replace old key file
  unlinkSync(key);
  renameSync(tmpKey, key);

  // Delete old key from GCP
  if (oldKeyId) {
    try {
      gcloudExec(
        `gcloud iam service-accounts keys delete ${oldKeyId} --iam-account=${email} --project ${project} --quiet`,
        true,
      );
    } catch { /* old key may already be expired */ }
  }

  writeLine(`  ${icons.success} ${colors.green}${squad}${RESET} credential rotated`);
  writeLine(`  ${colors.dim}New key: ${key}${RESET}`);
  writeLine();
}

async function listCredentials(): Promise<void> {
  ensureSecretsDir();
  const squadsDir = findSquadsDir();

  // Discover squads dynamically
  const squads: string[] = [];
  if (squadsDir) {
    const dirs = readdirSync(squadsDir).filter(d =>
      existsSync(join(squadsDir, d, 'SQUAD.md'))
    );
    squads.push(...dirs.sort());
  }

  if (squads.length === 0) {
    writeLine(`  ${colors.dim}No squads found. Run squads init first.${RESET}`);
    return;
  }

  writeLine();
  writeLine(`  ${bold}Squad Credentials${RESET}`);
  writeLine();
  writeLine(`  ${'Squad'.padEnd(16)} ${'Status'.padEnd(10)} ${'GCP Config'.padEnd(20)} Key`);
  writeLine(`  ${'-'.repeat(70)}`);

  for (const squad of squads) {
    const key = keyPath(squad);
    const perms = getSquadPermissions(squad);
    const hasKey = existsSync(key);
    const status = hasKey ? `${colors.green}active${RESET}` : `${colors.dim}none${RESET}  `;
    const config = perms ? `${perms.roles.length} roles` : `${colors.dim}not configured${RESET}`;

    writeLine(`  ${squad.padEnd(16)} ${status} ${config.padEnd(20)} ${hasKey ? '~/.squads/secrets/' + basename(key) : ''}`);
  }

  writeLine();
}

async function revokeCredential(squad: string): Promise<void> {
  const project = getProject();
  const email = saEmail(squad, project);
  const key = keyPath(squad);

  writeLine(`  ${bold}Revoking ${squad} credential${RESET}`);

  // Delete local key
  if (existsSync(key)) {
    unlinkSync(key);
    writeLine(`  ${colors.dim}Deleted local key${RESET}`);
  }

  // Delete all keys from GCP
  try {
    const keysJson = gcloudExec(
      `gcloud iam service-accounts keys list --iam-account=${email} --project ${project} --format=json 2>/dev/null`,
      true,
    );
    const keys = JSON.parse(keysJson);
    for (const k of keys) {
      if (k.keyType === 'USER_MANAGED') {
        gcloudExec(
          `gcloud iam service-accounts keys delete ${k.name.split('/').pop()} --iam-account=${email} --project ${project} --quiet`,
          true,
        );
      }
    }
    writeLine(`  ${colors.dim}Deleted remote keys${RESET}`);
  } catch { /* SA may not exist */ }

  // Delete service account
  try {
    gcloudExec(`gcloud iam service-accounts delete ${email} --project ${project} --quiet`);
    writeLine(`  ${colors.dim}Deleted service account${RESET}`);
  } catch { /* already deleted */ }

  writeLine(`  ${icons.success} ${colors.green}${squad}${RESET} credential revoked`);
  writeLine();
}

async function createAll(opts: { force?: boolean }): Promise<void> {
  const squadsDir = findSquadsDir();
  if (!squadsDir) {
    writeLine(`  ${icons.error} ${colors.red}No squads directory found.${RESET}`);
    return;
  }

  // Discover squads with credentials config
  const squads = readdirSync(squadsDir)
    .filter(d => existsSync(join(squadsDir, d, 'SQUAD.md')) && getSquadPermissions(d) !== null)
    .sort();

  if (squads.length === 0) {
    writeLine(`  ${colors.dim}No squads have credentials configured in SQUAD.md.${RESET}`);
    return;
  }

  writeLine(`  ${bold}Creating credentials for ${squads.length} squads${RESET}`);
  writeLine();

  for (const squad of squads) {
    await createCredential(squad, opts);
  }

  writeLine(`  ${bold}Done.${RESET} Run ${colors.cyan}squads credentials list${RESET} to verify.`);
  writeLine();
}

// ── Register ────────────────────────────────────────────────────────────

export function registerCredentialsCommand(program: Command): void {
  const creds = program
    .command('credentials')
    .description('Manage per-squad GCP service accounts and credentials');

  creds
    .command('create <squad>')
    .description('Create a service account and key for a squad')
    .option('--force', 'Recreate even if credential exists')
    .action(async (squad: string, opts) => {
      await createCredential(squad, opts);
    });

  creds
    .command('create-all')
    .description('Create credentials for all squads with GCP config in SQUAD.md')
    .option('--force', 'Recreate even if credentials exist')
    .action(async (opts) => {
      await createAll(opts);
    });

  creds
    .command('rotate <squad>')
    .description('Rotate a squad credential (create new key, delete old)')
    .action(async (squad: string) => {
      await rotateCredential(squad);
    });

  creds
    .command('list')
    .description('List all squad credentials and their status')
    .action(async () => {
      await listCredentials();
    });

  creds
    .command('revoke <squad>')
    .description('Delete a squad service account and all keys')
    .action(async (squad: string) => {
      await revokeCredential(squad);
    });
}

// ── Helper for execution engine ─────────────────────────────────────────

/**
 * Resolve the credential path for a squad. Returns the path to the
 * service account key file if it exists, or undefined.
 * Used by the execution engine to inject GOOGLE_APPLICATION_CREDENTIALS.
 */
export function resolveSquadCredential(squad: string): string | undefined {
  const key = keyPath(squad);
  return existsSync(key) ? key : undefined;
}
